import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Sidebar from '../../components/layout/Sidebar'
import { storageService } from '../../services'
import { API_BASE_URL, API_ENDPOINTS } from '../../constants'

// Dummy data ตาม Figma
const activityCategories = [
  { icon: '👨‍🌾', label: 'ข้อมูลของฉัน', color: 'bg-pink-100', route: '/profile' },
  { icon: '🏡', label: 'ข้อมูลฟาร์ม', color: 'bg-green-100', route: '/farms' },
  { icon: '🐄', label: 'ข้อมูลโค', color: 'bg-yellow-100', route: '/cows' },
  { icon: '🌾', label: 'การบันทึกอาหาร', color: 'bg-amber-100', route: '/feeding' },
  { icon: '⚖️', label: 'บันทึกน้ำหนักโค', color: 'bg-blue-100', route: '/cow-weight' },
  { icon: '💰', label: 'บัญชีรับจ่าย', color: 'bg-red-100', route: '/finance' },
  { icon: '📊', label: 'รายงาน-รวมข้าม', color: 'bg-purple-100', route: '/report' },
  { icon: '🏥', label: 'สุขภาพสัตว์', color: 'bg-orange-100', route: '/health' },
  { icon: '📈', label: 'ศึกษาข้อมูล', color: 'bg-yellow-100', route: '/learning' },
  { icon: '📰', label: 'ข่าวสาร', color: 'bg-orange-100', route: '/news' },
]

const recentNews = [
  {
    id: 1,
    title: 'พันธุ์โค FTA เปิดเสรีภาษีไทย-ออสเตรเลีย ระวังคนปกปิดเข้มข้น...',
    image: 'https://images.unsplash.com/photo-1560493676-04071c5f467b?w=400',
    badge: 'อ่านเพิ่ม',
  },
  {
    id: 2,
    title: 'ดวงไพรสู่โคเนื้อพันธุ์ข้าว...',
    image: 'https://images.unsplash.com/photo-1504006833117-8886a355efbf?w=400',
    badge: 'อ่านเพิ่ม',
  },
]

interface FarmerProfile {
  firstName: string
  lastName: string
}

interface Farm {
  id: string
  name: string
}

function HomePage() {
  const navigate = useNavigate()
  const [carouselIndex] = useState(0)
  const [farmerProfile, setFarmerProfile] = useState<FarmerProfile | null>(null)
  const [farms, setFarms] = useState<Farm[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const carouselImages = [
    'https://images.unsplash.com/photo-1560493676-04071c5f467b?w=1200&q=80',
    'https://images.unsplash.com/photo-1504006833117-8886a355efbf?w=1200&q=80',
    'https://images.unsplash.com/photo-1516467508483-a7212febe31a?w=1200&q=80',
  ]

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const token = storageService.getAuthToken()

      // Fetch farmer profile
      const profileResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.FARMERS.PROFILE}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (profileResponse.ok) {
        const profileData = await profileResponse.json()
        setFarmerProfile(profileData)
      }

      // Fetch farms
      const farmsResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.FARMS.BASE}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (farmsResponse.ok) {
        const farmsData = await farmsResponse.json()
        setFarms(farmsData)
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* Sidebar - ตาม Figma */}
      <Sidebar />

      {/* Main Content */}
      <div className="flex-1 pt-[64px]">
        {/* Header - ตาม Figma */}
        <div className="bg-white border-b border-border-light px-4 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            {/* Left Section */}
            <div className="flex items-center gap-4 lg:gap-8">
              {/* เกษตรกร Section */}
              <div>
                <h2 className="text-lg font-semibold text-text-primary">เกษตรกร</h2>
                <p className="text-sm text-text-secondary">
                  {isLoading ? 'กำลังโหลด...' : farmerProfile ? `สวัสดี, คุณ${farmerProfile.firstName} ${farmerProfile.lastName}` : 'สวัสดี'}
                </p>
              </div>

              {/* Divider */}
              <div className="h-12 w-px bg-border-light" />

              {/* หน้าแรก Section */}
              <div>
                <h2 className="text-lg font-semibold text-text-primary">หน้าแรก</h2>
                <p className="text-sm text-primary-800">
                  {isLoading ? 'กำลังโหลด...' : farms.length > 0 ? farms[0].name : 'ยังไม่มีฟาร์ม'}
                </p>
              </div>
            </div>

          </div>
        </div>

        {/* Content Area */}
        <div className="p-4 lg:p-8 space-y-6 bg-background-secondary min-h-screen">

          {/* รายการโปรด - ตาม Figma */}
          <div className="bg-white rounded-lg p-4 lg:p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4 lg:mb-6">
              <h2 className="text-base lg:text-lg font-semibold text-text-primary">รายการโปรด</h2>
              <button className="text-xs lg:text-sm text-primary-800 hover:text-primary-900 font-medium">
                แก้ไข
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 lg:gap-4">
              {activityCategories.map((category, index) => (
                <button
                  key={index}
                  onClick={() => navigate(category.route)}
                  className="flex flex-col items-center gap-1 lg:gap-2 p-2 lg:p-3 hover:bg-background-tertiary rounded-lg transition-all group"
                >
                  <div className={`w-10 h-10 lg:w-12 lg:h-12 ${category.color} rounded-full flex items-center justify-center text-xl lg:text-2xl group-hover:scale-110 transition-transform`}>
                    {category.icon}
                  </div>
                  <span className="text-[10px] lg:text-xs text-center text-text-secondary leading-tight">
                    {category.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Hero Carousel - ตาม Figma */}
          <div className="relative h-48 lg:h-64 lg:h-96 bg-white rounded-xl overflow-hidden shadow-sm">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url('${carouselImages[carouselIndex]}')`,
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />

            {/* Carousel Dots */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
              {carouselImages.map((_, index) => (
                <button
                  key={index}
                  className={`h-2 rounded-full transition-all ${
                    index === carouselIndex
                      ? 'bg-white w-8'
                      : 'bg-white/50 hover:bg-white/75 w-2'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* ความรู้เกี่ยวกับโค - ตาม Figma */}
          <div className="bg-white rounded-lg p-4 lg:p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4 lg:mb-6">
              <h2 className="text-base lg:text-lg font-semibold text-text-primary">ความรู้เกี่ยวกับโค</h2>
              <button className="text-xs lg:text-sm text-primary-800 hover:text-primary-900 font-medium">
                ดูทั้งหมด
              </button>
            </div>
            <p className="text-xs lg:text-sm text-text-secondary mb-4">แนะนำสตุดเอกสารสำหรับการเช้น</p>

            {/* Image Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
              {recentNews.map((news) => (
                <div key={news.id} className="group cursor-pointer">
                  <div className="relative aspect-video w-full bg-background-gray rounded-lg overflow-hidden mb-3">
                    <img
                      src={news.image}
                      alt={news.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <h3 className="text-sm font-normal text-text-primary mb-2 line-clamp-2">
                    {news.title}
                  </h3>
                  <button className="px-4 py-1.5 bg-primary-800 text-white text-xs font-medium rounded-lg hover:bg-primary-900 transition-colors">
                    {news.badge}
                  </button>

                  {/* Carousel Dots for news */}
                  <div className="flex gap-1 mt-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-text-tertiary" />
                    <div className="w-1.5 h-1.5 rounded-full bg-text-tertiary" />
                    <div className="w-1.5 h-1.5 rounded-full bg-text-tertiary" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HomePage
